import { useCallback, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { useAdminAuth } from '@/auth/use-admin-auth';
import {
  adjustUserAsset,
  banUser,
  deleteUserData,
  getUserDetail,
  isBanned,
  searchUserByNickname,
  setAdRemoval,
  unbanUser,
} from '@/client/admin.client';
import UserListBrowser from '@/components/admin/UserListBrowser';
import UserRecordsPanel from '@/components/admin/UserRecordsPanel';
import CurrencyTransactionHistoryPanel from '@/components/admin/CurrencyTransactionHistoryPanel';
import UserProgressDevPanel from '@/components/admin/UserProgressDevPanel';
import PassGrantPanel from '@/components/admin/PassGrantPanel';
import UserSpecTransferPanel from '@/components/admin/UserSpecTransferPanel';
import { downloadJson } from '@/lib/download';
import { isEthereumAddress, toChecksumAddress } from '@rb/shared/eip55';
import { findVerse } from '@rb/shared/admin-verses';
import { useActiveVerseStore } from '@/verse/active-verse.store';

interface UserDetailData {
  state: Record<string, unknown> | null;
  assets: Record<string, number>;
}

const ASSET_OPTIONS = [
  'gold',
  'ruby',
  'emerald',
  'sapphire',
  'stage_ticket',
  'league_ticket',
  'golden_blessing_token',
  'valhalla_ticket',
  'degen_ticket',
  'degen_silver_ticket',
  'degen_golden_ticket',
  'folkvangr_ticket',
  'thor_wrath',
  'blessing_of_freia',
  'glory',
  'breakthrough_stone_piece',
  'breakthrough_stone_low',
  'breakthrough_stone_mid',
  'breakthrough_stone_high',
  'skin_shard',
] as const;

interface BanStatus {
  banned: boolean;
  reason?: string;
  bannedAt?: number;
  bannedBy?: string;
}

export function UserManagement() {
  const { account } = useAdminAuth();
  // 프로덕션 verse 에서는 유저 데이터 삭제를 끈다 (서버도 prod 에서 hard-block).
  const activeVerseId = useActiveVerseStore((s) => s.activeVerseId);
  const isProdVerse = activeVerseId ? findVerse(activeVerseId)?.env === 'prod' : false;
  const [selectedAccount, setSelectedAccount] = useState<string | null>(null);
  const [detail, setDetail] = useState<UserDetailData | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchError, setSearchError] = useState<string | null>(null);

  const [deleteConfirmAccount, setDeleteConfirmAccount] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [banStatus, setBanStatus] = useState<BanStatus | null>(null);
  const [banLoading, setBanLoading] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [showBanConfirm, setShowBanConfirm] = useState(false);
  const [banActionLoading, setBanActionLoading] = useState(false);

  const [adjustAssetId, setAdjustAssetId] = useState<string>('gold');
  const [adjustDelta, setAdjustDelta] = useState<string>('100');
  const [adjusting, setAdjusting] = useState(false);
  const [adjustError, setAdjustError] = useState<string | null>(null);

  const [adRemovalLoading, setAdRemovalLoading] = useState(false);
  const [adRemovalError, setAdRemovalError] = useState<string | null>(null);

  const [reloadKey, setReloadKey] = useState(0);

  const fetchBanStatus = useCallback(async (account: string) => {
    setBanLoading(true);
    try {
      const result = await isBanned(account);
      setBanStatus(result);
    } catch {
      setBanStatus(null);
    } finally {
      setBanLoading(false);
    }
  }, []);

  const handleSearch = async () => {
    const input = searchInput.trim();
    if (!input) return;

    setDetailLoading(true);
    setDetail(null);
    setSelectedAccount(null);
    setBanStatus(null);
    setBanReason('');
    setShowBanConfirm(false);
    setSearchError(null);

    try {
      let account: string;
      if (isEthereumAddress(input)) {
        account = toChecksumAddress(input);
      } else {
        const result = await searchUserByNickname(input);
        if (!result.account) {
          setSearchError(`No user found with nickname matching "${input}"`);
          setDetailLoading(false);
          return;
        }
        account = result.account;
      }

      setSelectedAccount(account);
      const [result] = await Promise.all([getUserDetail(account), fetchBanStatus(account)]);

      if (!result.state && Object.keys(result.assets).length === 0) {
        setSearchError(`User not found: ${account}`);
        setSelectedAccount(null);
        setDetailLoading(false);
        return;
      }
      setDetail(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Search failed';
      setSearchError(message);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleBan = async () => {
    if (!selectedAccount) return;
    setBanActionLoading(true);
    try {
      await banUser(selectedAccount, banReason.trim() || undefined);
      setShowBanConfirm(false);
      setBanReason('');
      await fetchBanStatus(selectedAccount);
    } catch {
      // ignore
    } finally {
      setBanActionLoading(false);
    }
  };

  const handleUnban = async () => {
    if (!selectedAccount) return;
    setBanActionLoading(true);
    try {
      await unbanUser(selectedAccount);
      await fetchBanStatus(selectedAccount);
    } catch {
      // ignore
    } finally {
      setBanActionLoading(false);
    }
  };

  const handleAdjustAsset = async (sign: 1 | -1) => {
    if (!selectedAccount) return;
    setAdjustError(null);
    const n = Number(adjustDelta);
    if (!Number.isFinite(n) || n <= 0) {
      setAdjustError('amount must be > 0');
      return;
    }
    setAdjusting(true);
    try {
      await adjustUserAsset(selectedAccount, adjustAssetId, sign * n);
      const refreshed = await getUserDetail(selectedAccount);
      setDetail(refreshed);
    } catch (err) {
      setAdjustError(err instanceof Error ? err.message : String(err));
    } finally {
      setAdjusting(false);
    }
  };

  const handleSetAdRemoval = async (enabled: boolean) => {
    if (!selectedAccount) return;
    setAdRemovalError(null);
    setAdRemovalLoading(true);
    try {
      await setAdRemoval(selectedAccount, enabled);
      const refreshed = await getUserDetail(selectedAccount);
      setDetail(refreshed);
    } catch (err) {
      setAdRemovalError(err instanceof Error ? err.message : String(err));
    } finally {
      setAdRemovalLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmAccount) return;
    if (isProdVerse) return; // prod 에서는 삭제 불가 (서버도 차단)
    setDeleting(true);
    try {
      await deleteUserData(deleteConfirmAccount);
      setDeleteConfirmAccount(null);
      setSelectedAccount(null);
      setDetail(null);
      setReloadKey((k) => k + 1);
    } catch {
      // ignore
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <UserListBrowser reloadKey={reloadKey} />

      {/* Search bar */}
      <Card>
        <CardContent className="flex flex-col gap-3 pt-6">
          <div className="flex gap-2">
            <Input
              type="text"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setSearchError(null);
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search by account address (0x...) or nickname"
              className="flex-1"
            />
            {account && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setSearchInput(account);
                  setSearchError(null);
                }}
                title={account}
              >
                Me
              </Button>
            )}
            <Button
              type="button"
              onClick={handleSearch}
              disabled={!searchInput.trim() || detailLoading}
            >
              {detailLoading ? 'Searching…' : 'Search'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Accepts Ethereum addresses (auto-checksummed to EIP-55) or nicknames (e.g.
            &quot;Name#1234&quot;)
          </p>
        </CardContent>
      </Card>

      {searchError && (
        <Alert variant="destructive">
          <AlertDescription>{searchError}</AlertDescription>
        </Alert>
      )}

      {/* User detail panel */}
      {selectedAccount && (
        <Card>
          <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
            <div>
              <CardTitle className="text-base">User detail</CardTitle>
              <CardDescription className="font-mono break-all">{selectedAccount}</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => setDeleteConfirmAccount(selectedAccount)}
                disabled={isProdVerse}
                title={isProdVerse ? 'User deletion is disabled in production' : undefined}
              >
                Delete user data
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedAccount(null);
                  setDetail(null);
                  setSearchError(null);
                }}
              >
                Close
              </Button>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {/* Ban status */}
            {!banLoading && banStatus && (
              <div
                className={`rounded-md border p-3 ${
                  banStatus.banned
                    ? 'border-destructive/50 bg-destructive/10'
                    : 'border-border bg-muted/40'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  {banStatus.banned ? (
                    <Badge variant="destructive">BANNED</Badge>
                  ) : (
                    <span className="text-xs text-muted-foreground">Not banned</span>
                  )}
                  {banStatus.banned ? (
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleUnban}
                      disabled={banActionLoading}
                    >
                      {banActionLoading ? 'Processing…' : 'Unban user'}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => setShowBanConfirm(true)}
                    >
                      Ban user
                    </Button>
                  )}
                </div>
                {banStatus.banned && (
                  <div className="mt-2 flex flex-col gap-1 text-xs text-muted-foreground">
                    {banStatus.reason && (
                      <p>
                        <span className="text-foreground/60">Reason:</span> {banStatus.reason}
                      </p>
                    )}
                    {banStatus.bannedAt && (
                      <p>
                        <span className="text-foreground/60">Banned at:</span>{' '}
                        {new Date(banStatus.bannedAt).toLocaleString()}
                      </p>
                    )}
                    {banStatus.bannedBy && (
                      <p className="font-mono">
                        <span className="font-sans text-foreground/60">By:</span>{' '}
                        {banStatus.bannedBy}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {detailLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : detail ? (
              <>
                {/* Assets */}
                {Object.keys(detail.assets).length > 0 && (
                  <div className="flex flex-col gap-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Assets
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(detail.assets).map(([key, val]) => (
                        <Badge key={key} variant="secondary" className="font-normal">
                          {key}:&nbsp;<span className="font-bold">{val}</span>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <Separator />

                {/* Adjust asset (mint / burn) — staging·dev only (prod hidden + server-blocked) */}
                {!isProdVerse && (
                  <>
                    <div className="flex flex-col gap-2">
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Adjust asset
                      </h4>
                      <div className="flex flex-wrap items-end gap-2">
                        <div className="flex flex-col gap-1">
                          <Label htmlFor="adjust-asset" className="text-xs text-muted-foreground">
                            Asset
                          </Label>
                          <Select
                            value={adjustAssetId}
                            onValueChange={setAdjustAssetId}
                            disabled={adjusting}
                          >
                            <SelectTrigger id="adjust-asset" className="w-44">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ASSET_OPTIONS.map((id) => (
                                <SelectItem key={id} value={id}>
                                  {id}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex flex-col gap-1">
                          <Label htmlFor="adjust-delta" className="text-xs text-muted-foreground">
                            Amount
                          </Label>
                          <Input
                            id="adjust-delta"
                            type="number"
                            min={1}
                            value={adjustDelta}
                            onChange={(e) => setAdjustDelta(e.target.value)}
                            disabled={adjusting}
                            className="w-28"
                          />
                        </div>
                        <Button
                          type="button"
                          onClick={() => handleAdjustAsset(1)}
                          disabled={adjusting}
                          size="sm"
                        >
                          {adjusting ? '…' : 'Mint (+)'}
                        </Button>
                        <Button
                          type="button"
                          variant="destructive"
                          onClick={() => handleAdjustAsset(-1)}
                          disabled={adjusting}
                          size="sm"
                        >
                          {adjusting ? '…' : 'Burn (-)'}
                        </Button>
                      </div>
                      {adjustError && <p className="text-xs text-destructive">{adjustError}</p>}
                    </div>

                    <Separator />
                  </>
                )}

                {/* Ad removal toggle — staging·dev only (prod hidden + server-blocked) */}
                {!isProdVerse && (
                  <>
                    <div className="flex flex-col gap-2">
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        광고제거
                      </h4>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge
                          variant={
                            detail.state?.adRemovalPurchased === true ? 'default' : 'secondary'
                          }
                        >
                          {detail.state?.adRemovalPurchased === true ? '켜짐' : '꺼짐'}
                        </Badge>
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => handleSetAdRemoval(true)}
                          disabled={adRemovalLoading || detail.state?.adRemovalPurchased === true}
                        >
                          {adRemovalLoading ? '…' : '광고제거 ON'}
                        </Button>
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={() => handleSetAdRemoval(false)}
                          disabled={adRemovalLoading || detail.state?.adRemovalPurchased !== true}
                        >
                          {adRemovalLoading ? '…' : '광고제거 OFF'}
                        </Button>
                      </div>
                      {adRemovalError && (
                        <p className="text-xs text-destructive">{adRemovalError}</p>
                      )}
                    </div>

                    <Separator />
                  </>
                )}

                {/* Stage / tutorial progress override — staging·dev only (prod hidden + server-blocked) */}
                {!isProdVerse && (
                  <>
                    <UserProgressDevPanel
                      key={selectedAccount}
                      account={selectedAccount}
                      state={detail.state}
                      onApplied={() => void getUserDetail(selectedAccount).then(setDetail)}
                    />
                    <PassGrantPanel
                      key={`pass-grant-${selectedAccount}`}
                      account={selectedAccount}
                      state={detail.state}
                      onApplied={() => void getUserDetail(selectedAccount).then(setDetail)}
                    />
                    <Separator />
                  </>
                )}

                {/* Spec transfer — Export(전 env read) / Import(dev·staging only) */}
                <UserSpecTransferPanel
                  key={`spec-${selectedAccount}`}
                  account={selectedAccount}
                  state={detail.state}
                  canImport={!isProdVerse}
                  onApplied={() => void getUserDetail(selectedAccount).then(setDetail)}
                />
                <Separator />

                {/* State JSON */}
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between gap-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      State
                    </h4>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!detail?.state}
                      onClick={() =>
                        detail?.state &&
                        downloadJson(`user-${selectedAccount}-state.json`, detail.state)
                      }
                    >
                      Download state JSON
                    </Button>
                  </div>
                  <ScrollArea className="h-[300px] rounded-md border bg-muted/40">
                    <pre className="p-3 text-xs">
                      {detail.state ? JSON.stringify(detail.state, null, 2) : 'null'}
                    </pre>
                  </ScrollArea>
                </div>

                <Separator />

                {/* History / Suspicious Logs */}
                <UserRecordsPanel account={selectedAccount} />

                <Separator />

                {/* Currency transactions (mint/burn audit log) */}
                <CurrencyTransactionHistoryPanel account={selectedAccount} />
              </>
            ) : (
              <p className="text-sm text-muted-foreground">No data available</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Delete confirmation */}
      <AlertDialog
        open={deleteConfirmAccount !== null}
        onOpenChange={(open) => {
          if (!open && !deleting) setDeleteConfirmAccount(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">Delete user data</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span>Are you sure you want to delete all data for this user?</span>
              <code className="block break-all font-mono text-xs">{deleteConfirmAccount}</code>
              <span className="text-destructive">
                This deletes the user&apos;s record (state, assets, and ranking entries). The
                account re-initializes from scratch on next login. This action cannot be undone.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleConfirmDelete();
              }}
              disabled={deleting}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {deleting ? 'Deleting…' : 'Confirm delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Ban confirmation */}
      <AlertDialog
        open={showBanConfirm}
        onOpenChange={(open) => {
          if (!open && !banActionLoading) {
            setShowBanConfirm(false);
            setBanReason('');
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">Ban user</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span>Are you sure you want to ban this user?</span>
              <code className="block break-all font-mono text-xs">{selectedAccount}</code>
              <span className="text-destructive">
                The banned user will be unable to access any game features until unbanned.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ban-reason-input">Reason (optional)</Label>
            <Input
              id="ban-reason-input"
              type="text"
              value={banReason}
              onChange={(e) => setBanReason(e.target.value)}
              placeholder="Enter ban reason…"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={banActionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleBan();
              }}
              disabled={banActionLoading}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {banActionLoading ? 'Banning…' : 'Confirm ban'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default UserManagement;
